import React from 'react'
import type { SxProps, Theme } from '@mui/material'

type UseConstrainedTableContainerInput = {
  enabled: boolean
  containerId: string
  paginationRef: React.RefObject<HTMLDivElement>
  verticalSpacing?: number
  fallbackHeight?: number
  paginationVisible?: boolean
}

type UseConstrainedTableContainerOutput = {
  tableContainerProps: { sx: SxProps<Theme> }
  tableScrollWrapperSx: SxProps<Theme>
}

const DEFAULT_VERTICAL_SPACING = 32
const DEFAULT_FALLBACK_HEIGHT = 200

const useConstrainedTableContainer = ({
  enabled,
  containerId,
  paginationRef,
  verticalSpacing = DEFAULT_VERTICAL_SPACING,
  fallbackHeight = DEFAULT_FALLBACK_HEIGHT,
  paginationVisible = true,
}: UseConstrainedTableContainerInput): UseConstrainedTableContainerOutput => {
  const [tableScrollHeight, setTableScrollHeight] = React.useState<number | null>(null)

  const baseTableContainerSx = React.useMemo<SxProps<Theme>>(
    () => ({
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto',
      overflowY: 'auto',
    }),
    [],
  )

  const tableScrollWrapperSx = React.useMemo<SxProps<Theme>>(
    () => ({
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
    }),
    [],
  )

  const calculateHeight = React.useCallback(() => {
    if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const tableElement = document.getElementById(containerId)
    if (!tableElement) {
      return
    }

    const tableRect = tableElement.getBoundingClientRect()
    const paginationHeight = paginationVisible
      ? paginationRef.current?.getBoundingClientRect().height ?? 0
      : 0

    const availableHeight =
      window.innerHeight - tableRect.top - paginationHeight - verticalSpacing
    const normalizedHeight =
      availableHeight > 0 ? availableHeight : fallbackHeight

    setTableScrollHeight((prev) => (prev === normalizedHeight ? prev : normalizedHeight))
  }, [
    containerId,
    enabled,
    fallbackHeight,
    paginationRef,
    paginationVisible,
    verticalSpacing,
  ])

  const handleViewportChange = React.useCallback(() => {
    if (!enabled || typeof window === 'undefined') {
      return
    }

    window.requestAnimationFrame(() => {
      calculateHeight()
    })
  }, [calculateHeight, enabled])

  React.useLayoutEffect(() => {
    if (!enabled) {
      setTableScrollHeight(null)
      return
    }

    calculateHeight()
  }, [calculateHeight, enabled])

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return () => {}
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange)
    }
  }, [enabled, handleViewportChange])

  React.useEffect(() => {
    if (
      !enabled ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof ResizeObserver === 'undefined'
    ) {
      return () => {}
    }

    const observer = new ResizeObserver(() => {
      calculateHeight()
    })

    observer.observe(document.body)

    return () => {
      observer.disconnect()
    }
  }, [calculateHeight, enabled])

  const tableContainerProps = React.useMemo(() => {
    if (!enabled || tableScrollHeight === null) {
      return { sx: baseTableContainerSx }
    }

    return {
      sx: {
        ...baseTableContainerSx,
        height: `${tableScrollHeight}px`,
        maxHeight: `${tableScrollHeight}px`,
      },
    }
  }, [baseTableContainerSx, enabled, tableScrollHeight])

  return {
    tableContainerProps,
    tableScrollWrapperSx,
  }
}

export default useConstrainedTableContainer
